import { z } from 'zod';

// Common field schemas with validation
export const firstNameSchema = z
  .string()
  .min(1, 'First name is required')
  .max(50, 'First name must be less than 50 characters')
  .regex(
    /^[a-zA-Z\s'-]+$/,
    'First name can only contain letters, spaces, hyphens, and apostrophes'
  )
  .transform(val => val.trim());

export const lastNameSchema = z
  .string()
  .min(1, 'Last name is required')
  .max(50, 'Last name must be less than 50 characters')
  .regex(
    /^[a-zA-Z\s'-]+$/,
    'Last name can only contain letters, spaces, hyphens, and apostrophes'
  )
  .transform(val => val.trim());

export const middleNameSchema = z
  .string()
  .max(50, 'Middle name must be less than 50 characters')
  .regex(
    /^[a-zA-Z\s'-]*$/,
    'Middle name can only contain letters, spaces, hyphens, and apostrophes'
  )
  .optional()
  .transform(val => val?.trim() || '');

export const emailSchema = z
  .email('Invalid email address')
  .min(1, 'Email is required')
  .transform(val => val.toLowerCase().trim());

export const phoneNumberSchema = z
  .string()
  .min(1, 'Phone number is required')
  .regex(
    /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
    'Invalid phone number format'
  )
  .transform(val => val.replace(/[^\d+]/g, '')); // Keep only digits and plus

export const zipCodeSchema = z
  .string()
  .min(1, 'Zip code is required')
  .regex(
    /^\d{5}(-\d{4})?$/,
    'Invalid zip code format (e.g., 12345 or 12345-6789)'
  )
  .transform(val => val.trim());

export const addressSchema = z
  .string()
  .min(5, 'Address must be at least 5 characters')
  .max(100, 'Address must be less than 100 characters')
  .transform(val => val.trim());

export const citySchema = z
  .string()
  .min(1, 'City is required')
  .max(50, 'City must be less than 50 characters')
  .regex(/^[a-zA-Z\s]+$/, 'City can only contain letters and spaces')
  .transform(val => val.trim());

export const stateSchema = z
  .string()
  .min(2, 'State must be 2 characters')
  .max(2, 'State must be 2 characters')
  .regex(/^[A-Z]{2}$/, 'State must be 2 uppercase letters');

export const dateOfBirthSchema = z
  .string()
  .min(1, 'Date of birth is required')
  .regex(/^\d{2}\/\d{2}\/\d{4}$/, 'Date must be in MM/DD/YYYY format')
  .refine(val => {
    const [month, day, year] = val.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    const now = new Date();
    const minDate = new Date(now.getFullYear() - 100, 0, 1);

    return (
      date >= minDate &&
      date <= now &&
      date.getDate() === day &&
      date.getMonth() === month - 1 &&
      date.getFullYear() === year
    );
  }, 'Please enter a valid date of birth');

// Profile-related schemas
export const bioSchema = z
  .string()
  .max(500, 'Bio must be less than 500 characters')
  .transform(val => val.trim())
  .optional();

export const profilePicSchema = z
  .union([
    z.string().url('Profile picture must be a valid URL'),
    z
      .instanceof(File)
      .refine(
        file => file.size <= 5 * 1024 * 1024, // 5MB
        'Profile picture must be less than 5MB'
      )
      .refine(
        file => ['image/jpeg', 'image/png', 'image/webp'].includes(file.type),
        'Profile picture must be JPEG, PNG, or WebP'
      ),
  ])
  .optional();

export const languagesSchema = z
  .array(z.string())
  .min(1, 'At least one language is required')
  .max(10, 'Cannot select more than 10 languages');

export const workPreferencesSchema = z
  .array(z.string())
  .min(1, 'At least one work preference is required')
  .max(10, 'Cannot select more than 10 work preferences');

// Background check schemas
export const ssnSchema = z
  .string()
  .regex(
    /^\d{3}-\d{2}-\d{4}$|^\d{9}$/,
    'Invalid SSN format (e.g., 123-45-6789 or 123456789)'
  )
  .transform(val => val.replace(/[^\d]/g, ''));

export const distanceToTravelSchema = z
  .number()
  .min(1, 'Distance must be at least 1 mile')
  .max(100, 'Distance cannot exceed 100 miles')
  .default(3);

// Position/location schemas
export const positionAddressSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

export const middleNameInfoSchema = z.object({
  middleName: middleNameSchema,
  noMiddlename: z.boolean().default(true),
});

// Compound schemas
export const personalInfoSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  zipCode: zipCodeSchema,
  address: addressSchema,
  city: citySchema,
  state: stateSchema,
  middleNameInfo: middleNameInfoSchema,
  dateOfBirth: dateOfBirthSchema,
});

export const addressFormSchema = z.object({
  address_name: z
    .string()
    .max(100, 'Address name must be less than 100 characters')
    .optional(),
  address: addressSchema,
  city: citySchema,
  zipcode: zipCodeSchema,
  state: stateSchema,
  bedroom: z.number().min(0).max(20).optional(),
  bathroom: z.number().min(0).max(20).optional(),
  people: z.number().min(1).max(20).optional(),
  pet: z.number().min(0).max(10).optional(),
  cleaning_area: z
    .string()
    .max(1000, 'Cleaning area description is too long')
    .optional(),
  house_desc: z.string().max(1000, 'House description is too long').optional(),
});

export const cleanerProfileSchema = z.object({
  profilePic: profilePicSchema,
  bio: bioSchema,
  languages: languagesSchema,
  workPreferences: workPreferencesSchema,
  positionAddress: positionAddressSchema,
  distanceToTravel: distanceToTravelSchema,
});

export const backgroundCheckSchema = z.object({
  ssn: ssnSchema,
  sendBackgroundCheckCopy: z.boolean().default(false),
  backgroundCheckAccepted: z
    .boolean()
    .refine(val => val === true, 'You must accept the background check terms'),
});

// Export all schemas for easy importing
export const schemas = {
  // Field schemas
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  middleName: middleNameSchema,
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  zipCode: zipCodeSchema,
  address: addressSchema,
  city: citySchema,
  state: stateSchema,
  dateOfBirth: dateOfBirthSchema,
  bio: bioSchema,
  profilePic: profilePicSchema,
  languages: languagesSchema,
  workPreferences: workPreferencesSchema,
  ssn: ssnSchema,
  distanceToTravel: distanceToTravelSchema,

  // Compound schemas
  personalInfo: personalInfoSchema,
  addressForm: addressFormSchema,
  cleanerProfile: cleanerProfileSchema,
  backgroundCheck: backgroundCheckSchema,

  // Helper schemas
  middleNameInfo: middleNameInfoSchema,
  positionAddress: positionAddressSchema,
};
